import type {SuggestObject} from '@yandex-delivery/api/model/suggest'
import type {AddressSuggestParams as SuggestRequest} from '@yandex-delivery/api/service/suggest'
import type {LngLat} from '@yandex-delivery/blocks'
import {filter, firstOf, get, pipe, raise} from '@yandex-delivery/utils'

import type {LocationRequest} from '../../deprecated/fetch-detect-location-by-city'
import type {AddressPin} from '../../models/pickup-route'
import {toSimpleAddressPin, toUserAddressPin} from './mappers/toAddressPin'
import type {PickupRouteServiceParams} from './types'

type SuggestParams = {
  coordinates: LngLat
  text?: string
}
type ByCoordinatesParams = {
  coordinates: LngLat
  initial?: boolean
}
type BySuggestObjectParams = {
  coordinates: LngLat
  suggestObject: SuggestObject
}
type ByAddressPinParams = {
  addressPin: AddressPin
}
type AddressPinParams = ByCoordinatesParams | ByAddressPinParams | BySuggestObjectParams

export function createAddressService(params: PickupRouteServiceParams) {
  const {
    suggestService: {fetchAddressSuggest},
  } = params

  function _fetchSuggest(request: SuggestRequest): Promise<SuggestObject[]> {
    return fetchAddressSuggest(request).then(response => {
      return pipe(
        response, //
        get('__origin.results'),
        filter((result: SuggestObject) => result.action !== 'substitute'),
      )
    })
  }

  return {
    async fetchSuggest({coordinates, text}: SuggestParams): Promise<SuggestObject[]> {
      const location = coordinates as SuggestRequest['location']

      return _fetchSuggest({
        type: 'a',
        location,
        text,
      })
    },
    async fetchAddressPin(addressParams: AddressPinParams): Promise<AddressPin> {
      if ('addressPin' in addressParams) {
        return toUserAddressPin(addressParams.addressPin)
      }

      return _fetchSuggest(toSuggestParams(addressParams)).then(addresses => {
        return pipe(
          addresses,
          firstOf<SuggestObject[], SuggestObject>(
            // выбираем либо единственный адрес из саджеста
            suggest => (suggest.length === 1 ? suggest[0] : undefined),
            // либо, тот по которому уже кликали (который пришел в параметрах)
            suggest =>
              'suggestObject' in addressParams
                ? suggest.find(address => address.text === addressParams.suggestObject.text)
                : undefined,
            // либо, первый адрес
            suggest => suggest[0],
          ),
          (suggestObject = raise('[fetchAddressPin] "suggestObject" not found')): AddressPin => {
            return 'initial' in addressParams //
              ? toSimpleAddressPin(suggestObject)
              : toUserAddressPin(suggestObject)
          },
        )
      })
    },
  }
}

function toSuggestParams(params: AddressPinParams): SuggestRequest {
  if ('coordinates' in params) {
    const location = params.coordinates as LocationRequest['location']

    if ('suggestObject' in params) {
      return {
        type: 'a',
        location,
        __origin: params.suggestObject,
      }
    }

    return {
      type: 'a',
      location,
      point: location,
      // @ts-expect-error TS2322: Type '"pin_drop"' is not assignable to type 'SuggestAction | undefined'.
      action: 'pin_drop',
    }
  }

  raise('[toSuggestParams] not implemented yet\n' + JSON.stringify(params, null, 2))
}
